import { supabase } from "@/lib/supabase";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

async function authHeader() {
  let { data } = await supabase.auth.getSession();
  let session = data?.session;
  // Proactively refresh if the access token is expired or within 60s of expiry,
  // so mutations never fail with a spurious "Invalid session" (Sprint 4 #4/#5).
  const expMs = session?.expires_at ? session.expires_at * 1000 : 0;
  if (session && expMs && expMs - Date.now() < 60000) {
    try {
      const { data: r } = await supabase.auth.refreshSession();
      if (r?.session) session = r.session;
    } catch { /* fall through with existing token */ }
  }
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handle(res) {
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { detail: text }; }
  if (!res.ok) throw new Error(body.detail || body.message || "Request failed");
  return body;
}

export const api = {
  // driver verification
  verifyStart: (registration, mobile) =>
    fetch(`${API}/driver/verify/start`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ registration, mobile }),
    }).then(handle),

  verifyConfirm: (registration, mobile, code) =>
    fetch(`${API}/driver/verify/confirm`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ registration, mobile, code }),
    }).then(handle),

  // imports (staff, authenticated)
  importPreview: async (kind, file) => {
    const fd = new FormData(); fd.append("file", file);
    const path = kind === "allocation" ? "allocation" : kind === "f12" ? "f12" : kind === "ooh" ? "ooh" : "booking-update";
    return fetch(`${API}/imports/${path}/preview`, {
      method: "POST", headers: { ...(await authHeader()) }, body: fd,
    }).then(handle);
  },

  importApply: async (kind, batchId) => {
    const fd = new FormData(); fd.append("batch_id", batchId);
    const path = kind === "allocation" ? "allocation" : kind === "f12" ? "f12" : kind === "ooh" ? "ooh" : "booking-update";
    return fetch(`${API}/imports/${path}/apply`, {
      method: "POST", headers: { ...(await authHeader()) }, body: fd,
    }).then(handle);
  },

  // driver drop-off confirmation
  driverDropoff: async (bookingId) =>
    fetch(`${API}/driver/dropoff`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ booking_id: bookingId }),
    }).then(handle),

  // DEV-only quick login (env-gated server-side)
  devLoginEnabled: () => fetch(`${API}/dev/login/enabled`).then(handle),
  // Temporary shared site access lock (test/UAT gate)
  siteGateStatus: () => fetch(`${API}/site-gate/status`).then(handle),
  siteGateVerify: (password) =>
    fetch(`${API}/site-gate/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    }).then(handle),
  devLogin: (email) =>
    fetch(`${API}/dev/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }).then(handle),

  // ---- Sprint 2: admin business calendars / deadlines / security (admin only) ----
  adminListCalendars: async () =>
    fetch(`${API}/admin/calendars`, { headers: { ...(await authHeader()) } }).then(handle),
  adminSyncGovuk: async () =>
    fetch(`${API}/admin/calendars/sync-govuk`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  adminListHolidays: async (calId) =>
    fetch(`${API}/admin/calendars/${calId}/holidays`, { headers: { ...(await authHeader()) } }).then(handle),
  adminAddHoliday: async (payload) =>
    fetch(`${API}/admin/calendars/holidays`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  adminDeleteHoliday: async (id) =>
    fetch(`${API}/admin/calendars/holidays/${id}`, { method: "DELETE", headers: { ...(await authHeader()) } }).then(handle),
  adminLoginAudit: async () =>
    fetch(`${API}/admin/security/login-audit`, { headers: { ...(await authHeader()) } }).then(handle),
  adminBackfillPreview: async () =>
    fetch(`${API}/admin/deadlines/backfill/preview`, { headers: { ...(await authHeader()) } }).then(handle),
  adminBackfillApply: async () =>
    fetch(`${API}/admin/deadlines/backfill/apply`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),

  // ---- Sprint 3: notifications, digest, sync status ----
  staffNotify: async (payload) =>
    fetch(`${API}/staff/notify`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  adminDigestRun: async () =>
    fetch(`${API}/admin/digest/run`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  adminSyncStatus: async () =>
    fetch(`${API}/admin/calendars/sync-status`, { headers: { ...(await authHeader()) } }).then(handle),

  // ---- Sprint 4 ----
  adminExternalSummaryRun: async () =>
    fetch(`${API}/admin/external-summary/run`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  adminSetNotifyMode: async (cid, mode) =>
    fetch(`${API}/admin/customers/${cid}/notify-mode`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ mode }),
    }).then(handle),
  adminDigestTest: async (payload) =>
    fetch(`${API}/admin/digest/test`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  resendNotification: async (id) =>
    fetch(`${API}/staff/notifications/${id}/resend`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  publishView: async (payload) =>
    fetch(`${API}/staff/shared-views`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  listSharedViews: async () =>
    fetch(`${API}/staff/shared-views`, { headers: { ...(await authHeader()) } }).then(handle),
  unshareView: async (id) =>
    fetch(`${API}/staff/shared-views/${id}`, { method: "DELETE", headers: { ...(await authHeader()) } }).then(handle),
  copySharedView: async (id) =>
    fetch(`${API}/staff/shared-views/${id}/copy`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),

  // ---- Service/MOT confirmation workflow (staff) ----
  confIntake: async (payload) =>
    fetch(`${API}/staff/confirmations/intake`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  confList: async (status) =>
    fetch(`${API}/staff/confirmations${status ? `?status=${status}` : ""}`, { headers: { ...(await authHeader()) } }).then(handle),
  confDetail: async (cid) =>
    fetch(`${API}/staff/confirmations/${cid}`, { headers: { ...(await authHeader()) } }).then(handle),
  confMatch: async (cid, bookingRequestId) =>
    fetch(`${API}/staff/confirmations/${cid}/match`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ booking_request_id: bookingRequestId }),
    }).then(handle),
  confReject: async (cid, reason) =>
    fetch(`${API}/staff/confirmations/${cid}/reject`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ reason }),
    }).then(handle),
  confApply: async (cid) =>
    fetch(`${API}/staff/confirmations/${cid}/apply`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  bookingMarkSubmitted: async (bid, reference) =>
    fetch(`${API}/staff/bookings/${bid}/mark-submitted`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ confirm: true, external_booking_reference: reference || null }),
    }).then(handle),

  // ---- cross-pod cover (Sprint 5) ----
  coverMine: async () =>
    fetch(`${API}/staff/cover/mine`, { headers: { ...(await authHeader()) } }).then(handle),
  coverHistory: async (bookingId) =>
    fetch(`${API}/staff/cover/history?booking_id=${bookingId}`, { headers: { ...(await authHeader()) } }).then(handle),
  coverJoin: async (bid, reason) =>
    fetch(`${API}/staff/cover/${bid}/join`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ reason: reason || null }),
    }).then(handle),
  // Sprint 6: individual assignment (assign to me if no assigneeUserId; else reassign)
  assignBooking: async (bid, assigneeUserId) =>
    fetch(`${API}/staff/bookings/${bid}/assign`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ assignee_user_id: assigneeUserId || null }),
    }).then(handle),
  coverAssign: async (bid, staffUserId, reason) =>
    fetch(`${API}/staff/cover/${bid}/assign`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ staff_user_id: staffUserId, reason }),
    }).then(handle),
  coverEnd: async (coverId) =>
    fetch(`${API}/staff/cover/${coverId}/end`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  // Phase C: staff create request on behalf of a driver (any customer/pod; creator auto-assigned, scoped)
  staffBooking: async (payload) =>
    fetch(`${API}/staff/bookings/create`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  // Phase C: eligible AEs (active staff in a booking's owning pod) for "Assign to another AE"
  podAssignees: async (podId) =>
    fetch(`${API}/staff/pod-assignees?pod_id=${podId}`, { headers: { ...(await authHeader()) } }).then(handle),
  // Corrected model (0025): the caller's own active cover booking ids (service-scoped)
  myCover: async () =>
    fetch(`${API}/staff/my-cover`, { headers: { ...(await authHeader()) } }).then(handle),
  // Sprint 7 P1: Add Vehicle Note (canonical vehicle history; entered_by != source)
  addVehicleNote: async (payload) =>
    fetch(`${API}/staff/vehicle-notes`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  // CONF-AUD-001 supervisor audit + CONF-BULK-001 bulk intake
  confAudit: async (limit) =>
    fetch(`${API}/staff/confirmations/audit?limit=${limit || 200}`, { headers: { ...(await authHeader()) } }).then(handle),
  confBulkIntake: async (files) =>
    fetch(`${API}/staff/confirmations/bulk-intake`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ files }),
    }).then(handle),

  // ---- Driver Mode Round 1 (driver-authenticated) ----
  driverConfig: async () =>
    fetch(`${API}/driver/config`, { headers: { ...(await authHeader()) } }).then(handle),
  driverMileage: async (payload) =>
    fetch(`${API}/driver/mileage`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  driverDetails: async (email) =>
    fetch(`${API}/driver/details`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ email }),
    }).then(handle),
  driverChase: async (bookingId) =>
    fetch(`${API}/driver/chase`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ booking_id: bookingId }),
    }).then(handle),
  driverPushSubscribe: async (payload) =>
    fetch(`${API}/driver/push/subscribe`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  driverPushTest: async () =>
    fetch(`${API}/driver/push/test`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  driverAddresses: async () =>
    fetch(`${API}/driver/addresses`, { headers: { ...(await authHeader()) } }).then(handle),
  driverAddressAdd: async (payload) =>
    fetch(`${API}/driver/addresses`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  driverAddressDelete: async (aid) =>
    fetch(`${API}/driver/addresses/${aid}`, { method: "DELETE", headers: { ...(await authHeader()) } }).then(handle),
  // Sprint 7 Phase 3: weekly vehicle check
  driverVehicleCheckConfig: async () =>
    fetch(`${API}/driver/vehicle-check`, { headers: { ...(await authHeader()) } }).then(handle),
  driverSubmitVehicleCheck: async (payload) =>
    fetch(`${API}/driver/vehicle-check`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  staffAccidentGet: async (aid) =>
    fetch(`${API}/staff/accident/${aid}`, { headers: { ...(await authHeader()) } }).then(handle),
  // Sprint 7 Phase 7: expanded accident report (progressive claim)
  accidentCreate: async (payload) =>
    fetch(`${API}/driver/accident`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload || {}),
    }).then(handle),
  accidentGet: async (aid) =>
    fetch(`${API}/driver/accident/${aid}`, { headers: { ...(await authHeader()) } }).then(handle),
  accidentUpdate: async (aid, payload) =>
    fetch(`${API}/driver/accident/${aid}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  accidentAddParty: async (aid, payload) =>
    fetch(`${API}/driver/accident/${aid}/parties`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  accidentDeleteParty: async (aid, pid) =>
    fetch(`${API}/driver/accident/${aid}/parties/${pid}`, { method: "DELETE", headers: { ...(await authHeader()) } }).then(handle),
  accidentAddEvidence: async (aid, photos, captions, evidenceKind = "photo") =>
    fetch(`${API}/driver/accident/${aid}/evidence`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ photos, captions, evidence_kind: evidenceKind }),
    }).then(handle),
  accidentSubmit: async (aid) =>
    fetch(`${API}/driver/accident/${aid}/submit`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),

  // ---- Fleet Manager (client) operations ----
  fmBooking: async (payload) =>
    fetch(`${API}/fm/booking`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  fmChase: async (bookingId) =>
    fetch(`${API}/fm/chase`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ booking_id: bookingId }),
    }).then(handle),
  fmDispose: async (payload) =>
    fetch(`${API}/fm/dispose`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),

  // ---- Sprint 7 Phase 2/4: configurable forward-looking fleet dashboard ----
  fmDashboard: async (customerId) => {
    const p = customerId ? `?customer_id=${customerId}` : "";
    return fetch(`${API}/fm/dashboard${p}`, { headers: { ...(await authHeader()) } }).then(handle);
  },
  setDashboardConfig: async (payload) =>
    fetch(`${API}/staff/dashboard-config`, {
      method: "PUT", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  amCustomers: async () =>
    fetch(`${API}/staff/am-customers`, { headers: { ...(await authHeader()) } }).then(handle),
  // In-app alerts (weekly check digest + compliance reminders)
  alertsList: async () =>
    fetch(`${API}/alerts`, { headers: { ...(await authHeader()) } }).then(handle),
  alertRead: async (id) =>
    fetch(`${API}/alerts/${id}/read`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  alertReadAll: async () =>
    fetch(`${API}/alerts/read-all`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  alertsRun: async () =>
    fetch(`${API}/staff/alerts/run`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),
  // Sprint 7 Phase 6: compliance records
  complianceList: async (customerId) => {
    const p = customerId ? `?customer_id=${customerId}` : "";
    return fetch(`${API}/staff/compliance${p}`, { headers: { ...(await authHeader()) } }).then(handle);
  },
  complianceCreate: async (payload) =>
    fetch(`${API}/staff/compliance`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  complianceUpdate: async (rid, payload) =>
    fetch(`${API}/staff/compliance/${rid}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  complianceDelete: async (rid) =>
    fetch(`${API}/staff/compliance/${rid}`, { method: "DELETE", headers: { ...(await authHeader()) } }).then(handle),
  // Sprint 7 Phase 5: operational file ingestion
  opImportPreview: async (kind, file) => {
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    return fetch(`${API}/staff/operational-imports/preview`, {
      method: "POST", headers: { ...(await authHeader()) }, body: fd,
    }).then(handle);
  },
  opImportList: async () =>
    fetch(`${API}/staff/operational-imports`, { headers: { ...(await authHeader()) } }).then(handle),
  opImportApply: async (bid) =>
    fetch(`${API}/staff/operational-imports/${bid}/apply`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),

  // ---- Sprint 3: operational control layer ----
  opsStatus: () => fetch(`${API}/ops-status`).then(handle),
  driverResolveAllocation: async (vehicleId, mode) =>
    fetch(`${API}/driver/allocation/resolve`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ vehicle_id: vehicleId, mode }),
    }).then(handle),
  driverHistory: async () =>
    fetch(`${API}/driver/history`, { headers: { ...(await authHeader()) } }).then(handle),
  adminSetCapability: async (cid, payload) =>
    fetch(`${API}/admin/customers/${cid}/capabilities`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  adminSetLifecycle: async (cid, status) =>
    fetch(`${API}/admin/customers/${cid}/lifecycle`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ lifecycle_status: status }),
    }).then(handle),
  adminSetSettings: async (cid, payload) =>
    fetch(`${API}/admin/customers/${cid}/settings`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  ingestionIntake: async (payload) =>
    fetch(`${API}/staff/ingestion/intake`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  ingestionList: async (status, source) => {
    const p = new URLSearchParams(); if (status) p.set("status", status); if (source) p.set("source", source);
    return fetch(`${API}/staff/ingestion?${p}`, { headers: { ...(await authHeader()) } }).then(handle);
  },
  ingestionDetail: async (iid) =>
    fetch(`${API}/staff/ingestion/${iid}`, { headers: { ...(await authHeader()) } }).then(handle),
  ingestionMatch: async (iid, payload) =>
    fetch(`${API}/staff/ingestion/${iid}/match`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify(payload),
    }).then(handle),
  ingestionReject: async (iid, reason) =>
    fetch(`${API}/staff/ingestion/${iid}/reject`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ reason }),
    }).then(handle),
  ingestionApply: async (iid) =>
    fetch(`${API}/staff/ingestion/${iid}/apply`, { method: "POST", headers: { ...(await authHeader()) } }).then(handle),

  // ---- Evidence photo uploads (accident reports etc.) ----
  uploadBookingPhotos: async (bookingId, photos) =>
    fetch(`${API}/staff/bookings/${bookingId}/photos`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ photos }),
    }).then(handle),
  driverUploadBookingPhotos: async (bookingId, photos) =>
    fetch(`${API}/driver/bookings/${bookingId}/photos`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ photos }),
    }).then(handle),

  // ---- Vehicle audit PDF (Relay + legacy F12 history) ----
  vehicleAuditPdf: async (vehicleId) => {
    const res = await fetch(`${API}/staff/vehicle/${vehicleId}/audit.pdf`, {
      headers: { ...(await authHeader()) },
    });
    if (!res.ok) throw new Error("Could not generate the audit PDF.");
    return res.blob();
  },
};

// Read a File into a {name, content_type, data_base64} payload for photo upload.
export function fileToPhotoPayload(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, content_type: file.type, data_base64: String(reader.result) });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
